/**
 * What Claude asks a person beyond a tool permission (R-HL-6, ADR 0056): AskUserQuestion
 * questions, plan approvals (ExitPlanMode) and MCP elicitations. Pure: titles for rows and
 * cards, the answer a person builds and its checks before it goes to POST /api/approvals/:id,
 * and the "You answered" line after. The hub checks again (checkAskAnswer, the same function)
 * and answers 400 `ask-invalid` or `ask-scope`, which the answer UI shows inline.
 */
import { checkAskAnswer, isAskKind } from '@orgus/protocol';
import type { ApprovalAsk, ApprovalDecision, ApprovalRequest, AskKind, AskQuestion, ElicitationField, ElicitationSchema, PlanContinueMode } from '@orgus/protocol';

export type { AskKind };
export type QuestionsAsk = Extract<ApprovalAsk, { type: 'questions' }>;
export type PlanAsk = Extract<ApprovalAsk, { type: 'plan' }>;
export type ElicitationAsk = Extract<ApprovalAsk, { type: 'elicitation' }>;

/** The ask kind of a request ('question' for anything that is not a plan or a form). */
export const askKindOf = (a: Pick<ApprovalRequest, 'kind'>): AskKind => (a.kind === 'plan' ? 'plan' : a.kind === 'elicitation' ? 'elicitation' : 'question');

/** The ask a request carries when its kind and payload agree, else null (an older hub, a bad payload). */
export function askOf(a: Pick<ApprovalRequest, 'kind' | 'ask'>): ApprovalAsk | null {
  const ask = a.ask;
  if (!ask || !isAskKind(a.kind)) return null;
  if (a.kind === 'question' && ask.type === 'questions' && Array.isArray(ask.questions) && ask.questions.length > 0) return ask;
  if (a.kind === 'plan' && ask.type === 'plan' && typeof ask.plan === 'string') return ask;
  if (a.kind === 'elicitation' && ask.type === 'elicitation' && typeof ask.message === 'string') return ask;
  return null;
}

/** A question, plan or form answerable in place: its kind and payload agree. Anything else is a tool request. */
export const isAnswerable = (a: Pick<ApprovalRequest, 'kind' | 'ask'>): boolean => askOf(a) != null;

/** Up to the first line break, without Markdown marks, at most `max` characters. */
export function firstLine(text: string, max = 160): string {
  const line =
    text
      .split('\n')
      .map((l) => l.trim())
      .find(Boolean) ?? '';
  const plain = line
    .replace(/^#{1,6}\s+/, '')
    .replace(/^[-*>]\s+/, '')
    .replace(/[*_`]/g, '')
    .trim();
  return plain.length > max ? `${plain.slice(0, max - 1).trimEnd()}…` : plain;
}

/** A plan's title: its first heading, else its first line. */
export function planTitle(plan: string): string {
  const heading = /^\s{0,3}#{1,3}\s+(.+?)\s*#*\s*$/m.exec(plan);
  return firstLine(heading?.[1] ?? plan, 120) || 'Plan';
}

/** The plan without the heading its title came from, so the panel does not say it twice. */
export function planBody(plan: string): string {
  const lines = plan.split('\n');
  const i = lines.findIndex((l) => l.trim() !== '');
  if (i >= 0 && /^\s{0,3}#\s+/.test(lines[i]!)) return lines.slice(i + 1).join('\n').replace(/^\s*\n/, '');
  return plan;
}

/** What a row and a panel say about an ask. */
export interface AskSummary {
  kind: AskKind;
  /** Row title: the question, "Approve plan: <title>", or the server's message. */
  title: string;
  /** The one chip: the question's header (with +N for more questions). */
  chip?: string;
  /** The MCP server, for an elicitation. */
  server?: string;
}

export function askSummary(a: Pick<ApprovalRequest, 'kind' | 'ask' | 'title'>): AskSummary {
  const ask = askOf(a);
  const kind = askKindOf(a);
  if (!ask) return { kind, title: a.title };
  switch (ask.type) {
    case 'questions': {
      const [q, ...rest] = ask.questions;
      const header = q!.header?.trim();
      return { kind, title: firstLine(q!.question) || a.title, chip: header ? (rest.length ? `${header} +${rest.length}` : header) : undefined };
    }
    case 'plan':
      return { kind, title: `Approve plan: ${planTitle(ask.plan)}` };
    case 'elicitation':
      return { kind, title: firstLine(ask.message) || a.title, server: ask.server };
  }
}

/** Decided, in words, for the Done list. */
export function askDoneLabel(a: Pick<ApprovalRequest, 'kind' | 'state' | 'fallback' | 'handedBack'> & Partial<Pick<ApprovalRequest, 'sessionAnswer'>>): string {
  // Passed back, then answered in the session's own dialog (ADR 0056): what the session did.
  const sa = a.sessionAnswer;
  if (sa) {
    if (a.kind === 'plan') return sa.outcome === 'answered' ? 'Approved in the session' : 'Sent back in the session';
    return sa.outcome === 'answered' ? 'Answered in the session' : 'Skipped in the session';
  }
  if (a.handedBack) return 'Handed back';
  if (a.state === 'expired') return a.fallback === 'session' ? 'Asked in the session' : 'Expired';
  const allowed = a.state === 'allowed';
  if (a.kind === 'plan') return allowed ? 'Plan approved' : 'Sent back';
  if (a.kind === 'elicitation') return allowed ? 'Sent' : 'Declined';
  return allowed ? 'Answered' : 'Skipped';
}

/** What a person answered, as the decision carries it (before the hub confirms) or the request records it (after). */
export interface GivenAnswer {
  answer: 'allow' | 'deny';
  note?: string;
  ask?: ApprovalDecision['ask'];
}

/** The answer a decided request records: the session's own, when it was answered there. */
export function givenOf(a: Pick<ApprovalRequest, 'state' | 'note' | 'askAnswer'> & Partial<Pick<ApprovalRequest, 'sessionAnswer'>>): GivenAnswer {
  const sa = a.sessionAnswer;
  if (sa) return { answer: sa.outcome === 'answered' ? 'allow' : 'deny', ...(sa.note ? { note: sa.note } : {}), ...(sa.answers ? { ask: { answers: sa.answers } } : {}) };
  return { answer: a.state === 'allowed' ? 'allow' : 'deny', ...(a.note ? { note: a.note } : {}), ...(a.askAnswer ? { ask: a.askAnswer } : {}) };
}

const PLAN_MODE_WORD: Record<PlanContinueMode, string> = { default: 'ask before edits', acceptEdits: 'accept edits' };

/**
 * "You answered: Staging; Unit, Integration": what a person answered, in one line, for the card
 * that collapses after answering and the Done panel. `who` is "You" or the person's name.
 */
export function answerLine(ask: ApprovalAsk, g: GivenAnswer, who = 'You'): string {
  const note = g.note?.trim();
  if (ask.type === 'questions') {
    if (g.answer === 'deny') return note ? `${who} skipped the question: ${note}` : `${who} skipped the question`;
    const given = g.ask?.answers ?? {};
    const parts = ask.questions.map((q) => {
      const v = given[q.question]?.trim();
      if (!v) return null;
      return ask.questions.length > 1 && q.header?.trim() ? `${q.header.trim()} ${v}` : v;
    });
    const text = parts.filter(Boolean).join('; ');
    return text ? `${who} answered: ${text}` : `${who} answered`;
  }
  if (ask.type === 'plan') {
    if (g.answer === 'deny') return note ? `${who} kept Claude planning: ${note}` : `${who} kept Claude planning`;
    const mode = g.ask?.planMode;
    return mode ? `${who} approved the plan, ${PLAN_MODE_WORD[mode]}` : `${who} approved the plan`;
  }
  if (g.answer === 'deny') return note ? `${who} declined: ${note}` : `${who} declined`;
  if (ask.mode === 'url') return `${who} opened the link`;
  const content = g.ask?.content ?? {};
  const props = ask.schema?.properties ?? {};
  const parts = Object.entries(content).map(([k, v]) => {
    const f = props[k];
    const name = f?.title?.trim() || k;
    const i = typeof v === 'string' ? (f?.enum?.indexOf(v) ?? -1) : -1;
    const shown = typeof v === 'boolean' ? (v ? 'yes' : 'no') : i >= 0 && f?.enumNames?.[i] ? f.enumNames[i] : String(v);
    return `${name} ${shown}`;
  });
  return parts.length ? `${who} sent: ${parts.join('; ')}` : `${who} sent the form`;
}

/**
 * Each question's answer by its text, from Orgus or from the session, or null when none was
 * recorded (or content sharing kept the answers on the machine).
 */
export function questionAnswersOf(a: Pick<ApprovalRequest, 'state' | 'askAnswer'> & Partial<Pick<ApprovalRequest, 'sessionAnswer'>>): Record<string, string> | null {
  const sa = a.sessionAnswer;
  const given = sa ? (sa.outcome === 'answered' ? sa.answers : undefined) : a.state === 'allowed' ? a.askAnswer?.answers : undefined;
  return given && Object.keys(given).length ? given : null;
}

/**
 * Who decided, for the Done panel's Decided fact. A request passed back to the session never
 * reads "Nobody answered": it says it went to the session, or who answered there (ADR 0056).
 */
export function decidedText(a: Pick<ApprovalRequest, 'state' | 'decidedBy' | 'decidedFrom'> & Partial<Pick<ApprovalRequest, 'fallback' | 'handedBack' | 'sessionAnswer' | 'delegate' | 'onBehalfOf' | 'ruleId' | 'note'>>, who: (by: string | undefined) => string): string {
  const sa = a.sessionAnswer;
  // The pill says where (Answered in the session): the fact says who.
  if (sa) return sa.by ? who(sa.by) : 'The person at the session';
  if (a.onBehalfOf && !a.delegate) {
    const cause = a.ruleId === 'autonomy:full' ? 'Full autonomy' : a.ruleId === 'orgus:routine' ? 'Routine answer' : a.ruleId === 'session' ? 'Allow for session' : a.decidedBy === 'timeout' ? 'Expiry policy' : a.note ?? a.ruleId;
    return `Orgus, on behalf of ${a.onBehalfOf}${cause ? `, ${cause}` : ''}`;
  }
  // A delegate agent answered it (ADR 0074): the agent; the Policy fact names the policy.
  if (a.delegate) return `By ${a.delegate.agent}`;
  if (a.state === 'expired') {
    if (a.handedBack) return `Handed back by ${who(a.decidedBy)}`;
    return a.fallback === 'session' ? 'In the session' : 'Nobody answered in time';
  }
  if (a.decidedBy === 'policy') return 'By policy';
  return `By ${a.decidedBy ?? 'someone'}${a.decidedFrom && a.decidedFrom !== a.decidedBy ? ` from ${a.decidedFrom}` : ''}`;
}

/** When a decided request was settled: when the session answered it, else when Orgus decided. */
export const settledAt = (a: Pick<ApprovalRequest, 'createdAt' | 'decidedAt'> & Partial<Pick<ApprovalRequest, 'sessionAnswer'>>): number => a.sessionAnswer?.at ?? a.decidedAt ?? a.createdAt;

/** The hub's own check of an answer, run before sending so a mistake shows at once. Null when it fits. */
export function answerProblem(ask: ApprovalAsk, d: ApprovalDecision): string | null {
  const c = checkAskAnswer(ask, d.answer, d.ask);
  return c.ok ? null : `${c.why} ${c.next}`;
}

/** How long a card keeps "You answered" after the answer, before it leaves the drawer and the phone. */
export const ANSWERED_KEEP_MS = 10 * 60_000;

/** Questions, plans and forms decided by a person in the last ANSWERED_KEEP_MS, for the receipt cards. */
export function recentlyAnswered(recent: readonly ApprovalRequest[], now: number, sessionIds?: readonly string[]): ApprovalRequest[] {
  return recent.filter(
    (a) =>
      a.state !== 'pending' &&
      a.state !== 'expired' &&
      !a.handedBack &&
      isAnswerable(a) &&
      a.decidedBy != null &&
      !['policy', 'timeout', 'machine'].includes(a.decidedBy) &&
      now - (a.decidedAt ?? 0) < ANSWERED_KEEP_MS &&
      (!sessionIds || sessionIds.includes(a.sessionId)),
  );
}

/** Where else an ask can be answered, and what happens when nobody answers it: the card's last line. */
export function fallbackLine(a: Pick<ApprovalRequest, 'fallback' | 'expiresAt'>, surface: 'inbox' | 'drawer' | 'phone', clock: string): string {
  const elsewhere = surface === 'inbox' ? 'You can also answer it in the session drawer or on your phone.' : surface === 'drawer' ? 'You can also answer it in the Inbox or on your phone.' : 'You can also answer it in the Orgus console.';
  const after = a.fallback === 'session' ? `If nobody answers here by ${clock}, Claude asks in the session.` : `If nobody answers by ${clock}, it is declined.`;
  return `${elsewhere} ${after}`;
}

// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------

/** One question's answer so far: the chosen labels, and the free text when "Other" is chosen. */
export interface QuestionPick {
  labels: string[];
  /** null: Other is not chosen. A string (maybe empty): Other is chosen with this text. */
  other: string | null;
}

export type QuestionPicks = Record<number, QuestionPick>;

const NONE: QuestionPick = { labels: [], other: null };

/** Choose an option: replaces the choice of a single-choice question, toggles one of a multi-choice one. */
export function pickOption(q: Pick<AskQuestion, 'multiSelect'>, cur: QuestionPick | undefined, label: string): QuestionPick {
  const c = cur ?? NONE;
  if (!q.multiSelect) return { labels: [label], other: null };
  return { labels: c.labels.includes(label) ? c.labels.filter((l) => l !== label) : [...c.labels, label], other: c.other };
}

/** Choose or clear "Other". On a single-choice question it replaces the chosen option. */
export function pickOther(q: Pick<AskQuestion, 'multiSelect'>, cur: QuestionPick | undefined, on: boolean, text?: string): QuestionPick {
  const c = cur ?? NONE;
  const other = on ? (text ?? c.other ?? '') : null;
  return q.multiSelect ? { labels: c.labels, other } : { labels: on ? [] : c.labels, other };
}

export const isAnswered = (p: QuestionPick | undefined): boolean => !!p && (p.labels.length > 0 || (p.other != null && p.other.trim() !== ''));

/** The answer text Claude gets: the label, labels joined with ", " in option order, free text as typed. */
export function answerText(q: AskQuestion, p: QuestionPick): string {
  const labels = q.options.map((o) => o.label).filter((l) => p.labels.includes(l));
  const other = p.other?.trim();
  return [...labels, ...(other ? [other] : [])].join(', ');
}

/** Every question's answer keyed by its text, or the indexes still unanswered. */
export function questionAnswers(questions: readonly AskQuestion[], picks: QuestionPicks): { answers: Record<string, string>; missing: number[] } {
  const answers: Record<string, string> = {};
  const missing: number[] = [];
  questions.forEach((q, i) => {
    const p = picks[i];
    if (!isAnswered(p)) missing.push(i);
    else answers[q.question] = answerText(q, p!);
  });
  return { answers, missing };
}

/** "Answer Rollout and Tests first.": names the questions still open by their header chip. */
export function missingText(questions: readonly AskQuestion[], missing: readonly number[]): string {
  if (questions.length === 1) return 'Pick an answer first.';
  const names = missing.map((i) => questions[i]?.header?.trim() || `question ${i + 1}`);
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
  return `Answer ${list} first.`;
}

/** The next question without an answer after `from`, wrapping, or null when all are answered. */
export function nextUnanswered(count: number, picks: QuestionPicks, from: number): number | null {
  for (let k = 1; k <= count; k++) {
    const i = (from + k) % count;
    if (!isAnswered(picks[i])) return i;
  }
  return null;
}

export function questionsDecision(answers: Record<string, string>): ApprovalDecision {
  return { answer: 'allow', scope: 'once', ask: { answers } };
}

/** Dismiss a question, a plan's "Keep planning" and an elicitation's decline all answer deny. */
export function denyDecision(note?: string): ApprovalDecision {
  const n = note?.trim();
  return n ? { answer: 'deny', scope: 'once', note: n } : { answer: 'deny', scope: 'once' };
}

// ---------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------

export const PLAN_MODES: { value: PlanContinueMode; label: string }[] = [
  { value: 'default', label: 'Ask before edits' },
  { value: 'acceptEdits', label: 'Accept edits' },
];

export function planApproveDecision(mode: PlanContinueMode): ApprovalDecision {
  return { answer: 'allow', scope: 'once', ask: { planMode: mode } };
}

/** "Keep planning" needs feedback: null when the note is empty, so the UI can ask for it. */
export function keepPlanningDecision(note: string): ApprovalDecision | null {
  return note.trim() ? { answer: 'deny', scope: 'once', note: note.trim() } : null;
}

// ---------------------------------------------------------------------------
// Elicitations
// ---------------------------------------------------------------------------

export type FieldControl = 'text' | 'email' | 'url' | 'date' | 'datetime' | 'number' | 'integer' | 'checkbox' | 'radio' | 'select';

export interface FieldView {
  name: string;
  field: ElicitationField;
  label: string;
  required: boolean;
  control: FieldControl;
  /** For a choice: the values and their names. */
  options?: { value: string; label: string }[];
}

/** One control per schema property, in the server's order. Choices of up to 5 are radios, more a select. */
export function elicitationFields(schema: ElicitationSchema | undefined): FieldView[] {
  if (!schema?.properties) return [];
  const required = new Set(schema.required ?? []);
  return Object.entries(schema.properties).map(([name, field]) => {
    const label = field.title?.trim() || name;
    if (field.enum?.length) {
      const options = field.enum.map((v, i) => ({ value: String(v), label: field.enumNames?.[i] ?? String(v) }));
      return { name, field, label, required: required.has(name), control: options.length <= 5 ? 'radio' : 'select', options };
    }
    const control: FieldControl =
      field.type === 'boolean'
        ? 'checkbox'
        : field.type === 'integer'
          ? 'integer'
          : field.type === 'number'
            ? 'number'
            : field.format === 'email'
              ? 'email'
              : field.format === 'uri'
                ? 'url'
                : field.format === 'date'
                  ? 'date'
                  : field.format === 'date-time'
                    ? 'datetime'
                    : 'text';
    return { name, field, label, required: required.has(name), control };
  });
}

/** What the form holds while a person types: text for every field, booleans for checkboxes. */
export type FormValues = Record<string, string | boolean>;

export function initialValues(fields: readonly FieldView[]): FormValues {
  const out: FormValues = {};
  for (const f of fields) {
    const d = f.field.default;
    out[f.name] = f.control === 'checkbox' ? d === true : d == null ? '' : String(d);
  }
  return out;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;

/** Why a value is not accepted, in words that say how to fix it, or null. */
export function fieldError(f: FieldView, raw: string | boolean | undefined): string | null {
  if (f.control === 'checkbox') return null;
  const v = typeof raw === 'string' ? raw.trim() : '';
  if (!v) return f.required ? (f.options ? 'Pick one.' : 'Enter a value.') : null;
  const { field } = f;
  if (f.options) return f.options.some((o) => o.value === v) ? null : 'Pick one of the choices.';
  if (f.control === 'number' || f.control === 'integer') {
    const n = Number(v);
    if (!Number.isFinite(n)) return 'Enter a number.';
    if (f.control === 'integer' && !Number.isInteger(n)) return 'Enter a whole number.';
    if (field.minimum != null && n < field.minimum) return `Use ${String(field.minimum)} or more.`;
    if (field.maximum != null && n > field.maximum) return `Use ${String(field.maximum)} or less.`;
    return null;
  }
  if (field.minLength != null && v.length < field.minLength) return `Use at least ${String(field.minLength)} characters.`;
  if (field.maxLength != null && v.length > field.maxLength) return `Use at most ${String(field.maxLength)} characters.`;
  if (f.control === 'email' && !EMAIL.test(v)) return 'Enter an email address, like name@example.com.';
  if (f.control === 'url') {
    try {
      const u = new URL(v);
      if (!/^https?:$/.test(u.protocol)) return 'Enter a link that starts with https://.';
    } catch {
      return 'Enter a full link, like https://example.com.';
    }
  }
  if (f.control === 'date' && !DATE.test(v)) return 'Enter a date as YYYY-MM-DD.';
  if (f.control === 'datetime' && !DATETIME.test(v)) return 'Enter a date and time, like 2026-10-18T09:30.';
  return null;
}

/** The typed values for `ask.content`, or the errors by field. Empty optional fields are left out. */
export function elicitationContent(fields: readonly FieldView[], values: FormValues): { content: Record<string, string | number | boolean>; errors: Record<string, string> } {
  const content: Record<string, string | number | boolean> = {};
  const errors: Record<string, string> = {};
  for (const f of fields) {
    const raw = values[f.name];
    const err = fieldError(f, raw);
    if (err) {
      errors[f.name] = err;
      continue;
    }
    if (f.control === 'checkbox') {
      content[f.name] = raw === true;
      continue;
    }
    const v = typeof raw === 'string' ? raw.trim() : '';
    if (!v) continue;
    if (f.field.type === 'number' || f.field.type === 'integer') content[f.name] = Number(v);
    else if (f.field.type === 'boolean') content[f.name] = v === 'true';
    else content[f.name] = v;
  }
  return { content, errors };
}

export function elicitationDecision(content: Record<string, string | number | boolean>): ApprovalDecision {
  return { answer: 'allow', scope: 'once', ask: { content } };
}

/** A link from an MCP server opens only when it is http or https. */
export function safeLink(url: string | undefined): URL | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u : null;
  } catch {
    return null;
  }
}
